import { useState } from "react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useAdminLoad from "@/hooks/useAdminLoad";
import { Empty, LoadState, PageHeader } from "@/components/admin/AdminUI";
import ConfirmDialog from "@/components/admin/ConfirmDialog";
import GlassRow from "@/components/admin/GlassRow";
import { api } from "@/lib/api";

const STATUSES = [["live", "Live"], ["pending", "Being checked"], ["rejected", "Taken down"]];
const TYPES = [["all", "All"], ["voice", "Voice"], ["video", "Video"]];
const EMPTY = {
  live: ["No live intros", "Voice and video intros appear here as soon as members publish them."],
  pending: ["Nothing being checked", "Video intros wait here only while the automatic check runs."],
  rejected: ["Nothing taken down", "Intros you take down, or that fail the automatic check, appear here."],
};
const ASK = {
  reject: { title: "Take down this intro?", desc: "It leaves their profile at once. The member is told why and can upload a new one.", label: "Take down", destructive: true },
  restore: { title: "Put this intro back live?", desc: "It shows on their profile again straight away.", label: "Put back live", destructive: false },
};
const STALE = ["glass_replaced", "already_live", "already_rejected", "not_found"];

export default function GlassIntros() {
  const [status, setStatus] = useState("live");
  const [type, setType] = useState("all");
  const { data, error, loading, reload, setData } = useAdminLoad(`/admin/glass?status=${status}${type === "all" ? "" : `&type=${type}`}`);
  const [ask, setAsk] = useState(null);
  const [reason, setReason] = useState("");
  const reasons = data?.reasons || [];

  const run = async (g, action, why) => {
    try {
      const path = `/admin/glass/${g.user_id}/${action === "reject" ? "reject" : "approve"}`;
      await api(path, { body: action === "reject" ? { glass_id: g.glass_id, reason: why } : { glass_id: g.glass_id } });
      setData((d) => ({ ...d, items: d.items.filter((x) => x.user_id !== g.user_id), total: Math.max(0, (d.total || 1) - 1) }));
      toast.success(action === "reject" ? "Intro taken down. The member has been told." : "Intro is live again.");
    } catch (e) {
      toast.error(e.code === "glass_replaced" ? "This member has uploaded a new intro. Refresh." : e.message);
      if (STALE.includes(e.code)) reload();
    }
  };

  const onAct = (g, action) => {
    if (action === "approve") return run(g, "approve");
    setReason(reasons[0]?.key || "");
    setAsk({ g, action });
  };

  const [emptyTitle, emptyText] = EMPTY[status];
  return (
    <div data-testid="admin-glass-intros-page">
      <PageHeader title="Glass intros" description="Members' voice and video intros behind the glass." />
      <p className="cw-tone-neutral mb-6 rounded-2xl bg-[var(--cw-surface-tertiary)] px-4 py-3 text-sm" data-testid="glass-helper">
        Intros go live straight away. Take down anything that breaks the guidelines; the member is told and can upload a new one.
      </p>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Tabs value={status} onValueChange={setStatus}>
          <TabsList>{STATUSES.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`glass-tab-${k}`}>{l}</TabsTrigger>)}</TabsList>
        </Tabs>
        <Tabs value={type} onValueChange={setType}>
          <TabsList aria-label="Type">{TYPES.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`glass-type-filter-${k}`}>{l}</TabsTrigger>)}</TabsList>
        </Tabs>
      </div>
      <LoadState loading={loading && !data} error={error} onRetry={reload}>
        <p className="cw-muted mb-4 text-xs" data-testid="glass-total">{data?.total ?? 0} {STATUSES.find(([k]) => k === status)[1].toLowerCase()}</p>
        {data?.items?.length ? (
          <ul className="space-y-3" data-testid="glass-list">{data.items.map((g) => <GlassRow key={g.user_id} g={g} onAct={onAct} />)}</ul>
        ) : <Empty title={emptyTitle} text={type === "all" ? emptyText : `${emptyText} (showing ${type} only)`} testId={`glass-empty-${status}`} />}
      </LoadState>
      <ConfirmDialog open={!!ask} onOpenChange={(o) => !o && setAsk(null)} testId="glass-confirm-dialog"
        title={ask && ASK[ask.action].title} description={ask && ASK[ask.action].desc} confirmLabel={ask && ASK[ask.action].label}
        destructive={ask ? ASK[ask.action].destructive : false} confirmDisabled={ask?.action === "reject" && !reason}
        onConfirm={() => run(ask.g, ask.action, reason)}>
        {ask?.action === "reject" && (
          <div>
            <Label htmlFor="glass-reason">Reason the member sees</Label>
            <select id="glass-reason" value={reason} onChange={(e) => setReason(e.target.value)}
              className="mt-2 h-10 w-full rounded-lg border border-input bg-white px-2 text-sm" data-testid="glass-reason-select">
              {reasons.map((r) => <option key={r.key} value={r.key}>{r.text}</option>)}
            </select>
          </div>
        )}
      </ConfirmDialog>
    </div>
  );
}
